import React from "react";

import { Input, InputProps, Text, Flex } from "@chakra-ui/react";
import { debounce } from "lodash";
import * as Yup from "yup";

import { useAppSelector } from "store/store";

import { useAutoInputSend } from "../../../views/entidad/components/secciones/useAutoInputSend";

type ValidationErrors = {
	[key: string]: string | number | undefined;
};

type AdditionalProps = {
	name?: string;
	isDisabled?: boolean;
	defaultValue?: string | number;
	maxDecimals?: number;
};

type IInputFieldProps = InputProps & AdditionalProps;

// ─── sets de campos numéricos (fuera del componente para usarlos en la init) ───

const INTEGER_FIELDS = new Set([
	"derivados_caritas",
	"derivados_servicios_sociales",
	"derivados_propia_entidad",
	"sin_inf_derivados",
	"beneficiarios_0_18_meses",
	"beneficiarios_mas_de_18_meses",
	"beneficiarios_totales",
	"beneficiarios_efectivos",
	"n_familias",
	"n_meses_reparto",
	"media_personas_atendidas_mes_reparto",
	"porcentaje_propuesto_desayunos",
	"porcentaje_propuesto_comidas",
	"porcentaje_propuesto_meriendas",
	"porcentaje_propuesto_cenas",
	"porcentaje_desayunos",
	"porcentaje_comidas",
	"porcentaje_meriendas",
	"porcentaje_cenas",
	"numero_desayunos",
	"numero_comidas",
	"numero_meriendas",
	"numero_cenas",
	"dias_semana_desayunos",
	"dias_semana_comidas",
	"dias_semana_meriendas",
	"dias_semana_cenas",
	"unidades_desayuno",
	"unidades_comida",
	"unidades_merienda",
	"unidades_cena",
	"meses_actividad_ano",
	"tribal_beneficiarios_0_18_meses",
	"tribal_beneficiarios_mas_de_18_meses",
	"tribal_beneficiarios_totales",
	"tribal_n_familias",
	"guf_lactantes",
	"guf_adultos",
	"guf_beneficiarios",
	"guf_ufs",
	"numero_frigorificos",
	"numero_congeladores",
]);

const DECIMAL_FIELDS = new Set([
	"anoactcong",
	"anoactalcala",
	"anoactsf",
	"anoactalcorcon",
	"anoactmerca",
	"anoactfeadalcala",
	"anoactfyhalcala",
	"anoactfyhsf",
	"anoactfyhalcorcon",
	"total_recibido_bam",
	"factor_euros_kilos",
	"kilos_ano_medios_propios",
	"euros_ano_medios_propios",
	"kilos_ano_otras_aportaciones",
	"euros_ano_otras_aportaciones",
	"total_ano_otras_aportaciones",
	"kg_usuario_mes",
	"superficie_del_almacen",
	"capacidad_de_transporte",
	"m3_frigorifico",
	"m3_congelador",
	"maximo_kilos_fega_frutas_mes",
	"maximo_kilos_fega_hortalizas_mes",
	"maximo_kilos_fega_frutas_modificado",
	"maximo_kilos_fega_hortalizas_modificado",
	"factor_distribucion_modificado",
	"subvenciones_oficiales_importe",
	"ayudas_privadas_importe",
	"aportaciones_prestacion_importe",
	"ap6_otras_fuentes_importe",
	"total_ingresos_ano",
	"importe_salarios",
	"cargas_sociales_importe",
	"alquileres_servicios_importe",
	"compra_alimentos_importe",
	"otros_importe",
	"total_gastos_ano",
	"ingresos_gastos",
]);

const isFormattedField = (name?: string) =>
	name !== undefined && (INTEGER_FIELDS.has(name) || DECIMAL_FIELDS.has(name));

// "2567,5" → "2.567,5"  |  "1234" → "1.234"
const formatThousands = (raw: string): string => {
	if (!raw) return raw;
	const parts = raw.split(",");
	const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
	return parts.length > 1 ? `${intPart},${parts[1]}` : intPart;
};

// Dado el valor formateado y una posición en el valor sin puntos de miles,
// devuelve la posición equivalente en el valor formateado.
const findCursorInFormatted = (formatted: string, rawPos: number): number => {
	let count = 0;
	for (let i = 0; i < formatted.length; i++) {
		if (count === rawPos) return i;
		if (formatted[i] !== ".") count++;
	}
	return formatted.length;
};

// Convierte el valor que llega del backend (p.ej. 2567.5) al formato de display (2.567,5).
// Maneja todos los formatos posibles:
//   número  10055.22  → "10.055,22"
//   string  "10055.22" → "10.055,22"  (mysql2 DECIMAL como string)
//   string  "10.055,22" → "10.055,22" (ya formateado, no romper)
//   string  "10055,22"  → "10.055,22" (coma como decimal)
const toDisplayValue = (val: string | number | undefined, fieldName?: string): string | number | undefined => {
	if (val === undefined || val === null || val === "") return undefined;
	if (!isFormattedField(fieldName)) return val;

	const str = String(val);
	let raw: string;

	if (str.includes(",") && str.includes(".")) {
		// Ya tiene punto Y coma → punto = miles, coma = decimal → quitar puntos
		raw = str.replace(/\./g, "");
	} else if (str.includes(".")) {
		// Solo punto → es separador decimal → convertir a coma
		raw = str.replace(".", ",");
	} else {
		// Solo coma o sin separadores → ya está en formato raw
		raw = str;
	}

	return formatThousands(raw);
};

// ─── componente ───────────────────────────────────────────────────────────────

const InputField: React.FC<IInputFieldProps> = (props) => {
	const { name, isDisabled, defaultValue, maxDecimals, ...restProps } = props;
	const inputRef = React.useRef<HTMLInputElement>(null);
	const [inputValue, setInputValue] = React.useState<string | number | undefined>(() => {
		if (defaultValue === null || defaultValue === undefined || defaultValue === "") return undefined;
		return toDisplayValue(defaultValue, name);
	});

	React.useEffect(() => {
		if (document.activeElement === inputRef.current) return;
		if (defaultValue === null || defaultValue === undefined || defaultValue === "") {
			setInputValue(undefined);
		} else {
			setInputValue(toDisplayValue(defaultValue, name));
		}
	}, [defaultValue]);
	const evaluacion = useAppSelector((state) => state.evaluaciones.evaluacionSeleccionada.data);

	const { sendInputValue } = useAutoInputSend(
		evaluacion?.cod_eb.toString() || "",
		evaluacion?.numero_evaluacion.toString() || "",
	);

	const sendInputValueRef = React.useRef(sendInputValue);
	React.useEffect(() => {
		sendInputValueRef.current = sendInputValue;
	}, [sendInputValue]);

	const [errors, setErrors] = React.useState<ValidationErrors>({});

	const invalidCharsMessage = "No se permiten &, ?, = o %";

	const integerTransform = (_val: number, original: unknown) => {
		if (typeof original === "string") {
			if (original.trim() === "") return undefined;
			if (/[,.]/.test(original)) return NaN;
			return isNaN(Number(original)) ? NaN : Number(original);
		}
		return _val;
	};

	const decimalTransform = (_val: number, original: unknown) => {
		if (typeof original === "string") {
			if (original.trim() === "") return undefined;
			const parsed = parseFloat(original.replace(",", "."));
			return isNaN(parsed) ? NaN : parsed;
		}
		return _val;
	};

	const intField = Yup.number()
		.transform(integerTransform)
		.integer("Debe ser un número entero")
		.min(0, "El valor mínimo es 0")
		.typeError("Introduce un número entero");

	const floatField = Yup.number()
		.transform(decimalTransform)
		.min(0, "El valor mínimo es 0")
		.typeError("Introduce un número válido");

	const validationSchema = Yup.object().shape({
		// Sección 2
		derivados_caritas: intField,
		derivados_servicios_sociales: intField,
		derivados_propia_entidad: intField,
		sin_inf_derivados: intField,

		// Sección 3 — beneficiarios
		beneficiarios_0_18_meses: intField,
		beneficiarios_mas_de_18_meses: intField,
		beneficiarios_totales: intField,
		beneficiarios_efectivos: intField,
		n_familias: intField,
		n_meses_reparto: intField,
		media_personas_atendidas_mes_reparto: intField,
		porcentaje_propuesto_desayunos: intField.max(100, "El porcentaje máximo es 100"),
		porcentaje_propuesto_comidas: intField.max(100, "El porcentaje máximo es 100"),
		porcentaje_propuesto_meriendas: intField.max(100, "El porcentaje máximo es 100"),
		porcentaje_propuesto_cenas: intField.max(100, "El porcentaje máximo es 100"),
		porcentaje_desayunos: intField.max(100, "El porcentaje máximo es 100"),
		porcentaje_comidas: intField.max(100, "El porcentaje máximo es 100"),
		porcentaje_meriendas: intField.max(100, "El porcentaje máximo es 100"),
		porcentaje_cenas: intField.max(100, "El porcentaje máximo es 100"),
		numero_desayunos: intField,
		numero_comidas: intField,
		numero_meriendas: intField,
		numero_cenas: intField,
		dias_semana_desayunos: intField.max(7, "El número máximo de días es 7"),
		dias_semana_comidas: intField.max(7, "El número máximo de días es 7"),
		dias_semana_meriendas: intField.max(7, "El número máximo de días es 7"),
		dias_semana_cenas: intField.max(7, "El número máximo de días es 7"),
		unidades_desayuno: intField,
		unidades_comida: intField,
		unidades_merienda: intField,
		unidades_cena: intField,
		meses_actividad_ano: intField.min(1, "El número mínimo de meses es 1").max(12, "El número máximo de meses es 12"),

		// Sección 3 — tribal / GUF
		tribal_beneficiarios_0_18_meses: intField,
		tribal_beneficiarios_mas_de_18_meses: intField,
		tribal_beneficiarios_totales: intField,
		tribal_n_familias: intField,
		guf_lactantes: intField,
		guf_adultos: intField,
		guf_beneficiarios: intField,
		guf_ufs: intField,

		// Sección 5 — kg BAM (float)
		anoactcong: floatField,
		anoactalcala: floatField,
		anoactsf: floatField,
		anoactalcorcon: floatField,
		anoactmerca: floatField,
		anoactfeadalcala: floatField,
		anoactfyhalcala: floatField,
		anoactfyhsf: floatField,
		anoactfyhalcorcon: floatField,
		total_recibido_bam: floatField,

		// Sección 6 — otras aportaciones (decimal 10,2)
		factor_euros_kilos: floatField,
		kilos_ano_medios_propios: floatField,
		euros_ano_medios_propios: floatField,
		kilos_ano_otras_aportaciones: floatField,
		euros_ano_otras_aportaciones: floatField,
		total_ano_otras_aportaciones: floatField,

		// Sección 7
		kg_usuario_mes: floatField,

		// Sección 8 — logística
		superficie_del_almacen: floatField,
		numero_frigorificos: intField,
		numero_congeladores: intField,
		capacidad_de_transporte: floatField,
		m3_frigorifico: floatField,
		m3_congelador: floatField,
		maximo_kilos_fega_frutas_mes: floatField,
		maximo_kilos_fega_hortalizas_mes: floatField,
		maximo_kilos_fega_frutas_modificado: floatField,
		maximo_kilos_fega_hortalizas_modificado: floatField,
		factor_distribucion_modificado: floatField,

		// Sección 9 — importes económicos (decimal 18,2)
		subvenciones_oficiales_importe: floatField,
		ayudas_privadas_importe: floatField,
		aportaciones_prestacion_importe: floatField,
		ap6_otras_fuentes_importe: floatField,
		total_ingresos_ano: floatField,
		importe_salarios: floatField,
		cargas_sociales_importe: floatField,
		alquileres_servicios_importe: floatField,
		compra_alimentos_importe: floatField,
		otros_importe: floatField,
		total_gastos_ano: floatField,
		ingresos_gastos: floatField,

		// Textos con caracteres no permitidos
		email: Yup.string()
			.email("El correo electrónico no es válido")
			.matches(/^[^&?=%]*$/, invalidCharsMessage),
		email_responsable: Yup.string()
			.email("El correo electrónico no es válido")
			.matches(/^[^&?=%]*$/, invalidCharsMessage),
		direccion_actividad: Yup.string().matches(/^[^&?=%]*$/, invalidCharsMessage),
		municipio_actividad: Yup.string().matches(/^[^&?=%]*$/, invalidCharsMessage),
		codpost_actividad: Yup.string().matches(/^[^&?=%]*$/, invalidCharsMessage),
		telefono_actividad: Yup.string().matches(/^[^&?=%]*$/, invalidCharsMessage),
		direccion_postal_direccion_actividad: Yup.string().matches(/^[^&?=%]*$/, invalidCharsMessage),
		direccion_postal_municipio_actividad: Yup.string().matches(/^[^&?=%]*$/, invalidCharsMessage),
		direccion_postal_codpost_actividad: Yup.string().matches(/^[^&?=%]*$/, "No se permiten &, ?, =, %"),
		direccion_postal_telefono_actividad: Yup.string().matches(/^[^&?=%]*$/, "No se permiten &, ?, =, %"),
		nombre_responsable: Yup.string().matches(/^[^&?=%]*$/, invalidCharsMessage),
		apellidos_responsable: Yup.string().matches(/^[^&?=%]*$/, invalidCharsMessage),
		cargo_responsable: Yup.string().matches(/^[^&?=%]*$/, invalidCharsMessage),
		telefono_responsable: Yup.string().matches(/^[^&?=%]*$/, invalidCharsMessage),
		nombre_contacto: Yup.string().matches(/^[^&?=%]*$/, invalidCharsMessage),
		apellidos_contacto: Yup.string().matches(/^[^&?=%]*$/, invalidCharsMessage),
		cargo_contacto: Yup.string().matches(/^[^&?=%]*$/, invalidCharsMessage),
		email_contacto: Yup.string().matches(/^[^&?=%]*$/, invalidCharsMessage),
		telefono_contacto: Yup.string().matches(/^[^&?=%]*$/, invalidCharsMessage),
	});

	const validateInput = async (name: string, value: string | number | undefined): Promise<boolean> => {
		if (maxDecimals !== undefined && typeof value === "string") {
			const commaIndex = value.indexOf(",");
			if (commaIndex !== -1 && value.length - commaIndex - 1 > maxDecimals) {
				const msg =
					maxDecimals === 0
						? "Este campo no admite decimales"
						: `Máximo ${maxDecimals} decimal${maxDecimals !== 1 ? "es" : ""}`;
				setErrors((errors) => ({ ...errors, [name]: msg }));
				return false;
			}
		}

		if (name && validationSchema.fields[name]) {
			try {
				await validationSchema.fields[name].validate(value);
				setErrors((errors) => ({ ...errors, [name]: undefined }));
				return true;
			} catch (err) {
				setErrors((errors) => ({ ...errors, [name]: (err as Yup.ValidationError).message }));
				return false;
			}
		}

		if (name) setErrors((errors) => ({ ...errors, [name]: undefined }));
		return true;
	};

	const debouncedChangeHandler = React.useMemo(
		() => debounce((name: string, value: string | number | null) => sendInputValueRef.current(name, value), 700),
		[],
	);

	const showTempError = (msg: string) => {
		if (!name) return;
		setErrors((errors) => ({ ...errors, [name]: msg }));
		setTimeout(() => setErrors((errors) => ({ ...errors, [name]: undefined })), 2000);
	};

	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;

		const isNumericField = !!(name && validationSchema.fields[name]?.type === "number") || maxDecimals !== undefined;
		const isPaste = (event.nativeEvent as InputEvent).inputType === "insertFromPaste";

		// ── Normalización del valor según el tipo de campo ───────────────────
		let rawValue = value;

		if (name && isFormattedField(name)) {
			if (isPaste) {
				// Paste en campo decimal: normalizar separadores
				if (DECIMAL_FIELDS.has(name)) {
					rawValue =
						value.includes(",") && value.includes(".")
							? value.replace(/\./g, "") // 2.567,5 → 2567,5
							: value.replace(/\./g, ","); // 2567.5  → 2567,5
				}
				// Paste en campo entero: quitar todo desde el primer separador
				if (INTEGER_FIELDS.has(name)) {
					rawValue = value.replace(/[,.].*$/, "");
				}
			} else {
				// Escritura normal: quitar los puntos de miles que ya estaban
				rawValue = value.replace(/\./g, "");
			}
		}

		// ── maxDecimals ───────────────────────────────────────────────────────
		if (maxDecimals !== undefined) {
			const commaIndex = rawValue.indexOf(",");
			if (commaIndex !== -1 && rawValue.length - commaIndex - 1 > maxDecimals) {
				if (isPaste && name) {
					const msg =
						maxDecimals === 0
							? "Este campo no admite decimales"
							: `Máximo ${maxDecimals} decimal${maxDecimals !== 1 ? "es" : ""}`;
					setErrors((errors) => ({ ...errors, [name]: msg }));
				}
				return;
			}
		}

		// ── Formato display con puntos de miles ───────────────────────────────
		const displayValue = name && isFormattedField(name) ? formatThousands(rawValue) : rawValue;

		// ── Ajuste de cursor (salta por encima de los puntos de miles) ────────
		if (name && isFormattedField(name) && inputRef.current) {
			const cursorInBrowser = event.target.selectionStart ?? value.length;
			const dotsBeforeCursor = (value.slice(0, cursorInBrowser).match(/\./g) || []).length;
			const rawCursor = cursorInBrowser - dotsBeforeCursor;
			const newCursor = findCursorInFormatted(displayValue, rawCursor);
			requestAnimationFrame(() => {
				if (inputRef.current) {
					inputRef.current.selectionStart = newCursor;
					inputRef.current.selectionEnd = newCursor;
				}
			});
		}

		setInputValue(displayValue);

		// ── Campo vacío: enviar 0 para campos numéricos ──────────────────────
		if (isNumericField && rawValue === "") {
			debouncedChangeHandler(name, 0);
			if (name) setErrors((errors) => ({ ...errors, [name]: "Este campo es obligatorio" }));
			return;
		}

		// ── Validar y enviar ──────────────────────────────────────────────────
		validateInput(name, rawValue).then((isValid) => {
			if (!isValid) return;
			if (isNumericField) {
				const asNumber = parseFloat(rawValue.replace(",", "."));
				debouncedChangeHandler(name, isNaN(asNumber) ? rawValue : asNumber);
			} else {
				debouncedChangeHandler(name, rawValue);
			}
		});
	};

	const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
		const position = event.target.value.length;
		event.target.selectionStart = position;
		event.target.selectionEnd = position;
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (!name) return;
		if (INTEGER_FIELDS.has(name) && (event.key === "," || event.key === ".")) {
			event.preventDefault();
			showTempError("No se permiten decimales en este campo");
		} else if (DECIMAL_FIELDS.has(name) && event.key === ".") {
			event.preventDefault();
			showTempError("Usa coma (,) como separador decimal");
		}
	};

	return (
		<Flex direction="column" width="100%" alignItems="flex-start" position="relative">
			<Input
				name={name}
				value={inputValue}
				ref={inputRef}
				isDisabled={isDisabled}
				{...restProps}
				onChange={handleInputChange}
				onFocus={handleFocus}
				onKeyDown={handleKeyDown}
				borderColor={name && errors[name] ? "red" : "gray.300"}
			/>
			{name && errors[name] && (
				<Text textStyle="body4" color="red" position="absolute" bottom="-20px" left="0">
					{errors[name]}
				</Text>
			)}
		</Flex>
	);
};

export default InputField;
